import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Mic, MicOff, Wifi, WifiOff, 
  AlertCircle, CheckCircle, Loader2,
  Globe, Shield
} from 'lucide-react';
import { cn } from '@/lib/utils';
import axios from 'axios';
import PCMCapture from '@/lib/audio-capture';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const industryColors = {
  Medical: '#EF4444',
  Legal: '#3B82F6', 
  AgedCare: '#10B981'
};

const industryBadgeColors = {
  Medical: 'bg-red-500/20 text-red-400 border-red-500/30',
  Legal: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  AgedCare: 'bg-green-500/20 text-green-400 border-green-500/30'
};

const ClientIntake = () => {
  const { token } = useParams();
  
  // Session validation state
  const [sessionData, setSessionData] = useState(null);
  const [isValidating, setIsValidating] = useState(true);
  const [validationError, setValidationError] = useState(null);
  
  // Audio streaming state
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [error, setError] = useState(null);
  const [isThinking, setIsThinking] = useState(false);
  
  // Refs for audio handling
  const wsRef = useRef(null);
  const captureRef = useRef(null);
  const audioContextRef = useRef(null);
  const playbackCursorRef = useRef(0);
  
  // Validate session token on mount
  useEffect(() => {
    const validateSession = async () => {
      try {
        setIsValidating(true);
        const response = await axios.get(`${API}/sessions/${token}/validate`);
        setSessionData(response.data);
        setValidationError(null);
      } catch (err) {
        console.error('Session validation failed:', err);
        setValidationError(
          err.response?.data?.detail || 
          'This link is invalid or has expired. Please contact your provider for a new link.'
        );
      } finally {
        setIsValidating(false);
      }
    };
    
    if (token) {
      validateSession();
    }
  }, [token]);
  
  // Get WebSocket URL for client sessions
  const getWebSocketUrl = useCallback(() => {
    if (BACKEND_URL) {
      const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      return `${WS_URL}/ws/client/${token}`;
    }
    // Production: derive from current page origin
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws/client/${token}`;
  }, [token]);
  
  // Play received PCM audio (24 kHz mono from Gemini Live), scheduled back-to-back
  const playAudio = async (base64Audio) => {
    try {
      if (!audioContextRef.current) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AudioCtx({ sampleRate: 24000 });
        playbackCursorRef.current = audioContextRef.current.currentTime;
      }
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') await ctx.resume();

      const binary = atob(base64Audio);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

      const sampleCount = bytes.length / 2;
      const audioBuffer = ctx.createBuffer(1, sampleCount, 24000);
      const channelData = audioBuffer.getChannelData(0);
      const dv = new DataView(bytes.buffer);
      for (let i = 0; i < sampleCount; i++) {
        channelData[i] = dv.getInt16(i * 2, true) / 0x8000;
      }

      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);
      const startAt = Math.max(ctx.currentTime, playbackCursorRef.current);
      source.start(startAt);
      playbackCursorRef.current = startAt + audioBuffer.duration;
    } catch (err) {
      console.error('Error playing audio:', err);
    }
  };
  
  // Handle WebSocket messages (adapted from useAudioStream)
  const handleMessage = useCallback(async (event) => {
    try {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case 'session_started':
          console.log('Client session started:', data.session_id);
          break;
          
        case 'audio':
          if (data.audio) {
            await playAudio(data.audio);
          }
          break;
          
        case 'transcript':
          setTranscript(prev => {
            const incoming = data.text || '';
            const last = prev[prev.length - 1];
            // Merge consecutive streaming fragments from the same speaker so the
            // conversation reads naturally instead of one word per line.
            if (last && last.speaker === data.speaker && last.streaming) {
              const merged = { ...last, text: (last.text || '') + incoming };
              return [...prev.slice(0, -1), merged];
            }
            return [...prev, {
              id: Date.now() + Math.random(),
              text: incoming,
              speaker: data.speaker,
              timestamp: new Date().toISOString(),
              type: 'message',
              streaming: true
            }];
          });
          setIsThinking(false);
          break;
          
        case 'turn_complete':
          console.log('Turn complete');
          setIsThinking(true);
          // Finalize current streaming message so the next turn starts a new bubble.
          setTranscript(prev => {
            if (!prev.length) return prev;
            const last = prev[prev.length - 1];
            if (last.streaming) {
              return [...prev.slice(0, -1), { ...last, streaming: false }];
            }
            return prev;
          });
          if (audioContextRef.current) {
            playbackCursorRef.current = audioContextRef.current.currentTime;
          }
          break;
          
        case 'interrupted':
          console.log('Interrupted');
          setIsThinking(false);
          break;
          
        case 'error':
          setError(data.message || 'Unknown error occurred');
          console.error('WebSocket error:', data.message);
          setIsThinking(false);
          break;
          
        default:
          console.log('Unknown message type:', data.type, data);
      }
    } catch (err) {
      console.error('Error parsing WebSocket message:', err);
    }
  }, []);
  
  // Connect to WebSocket
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      disconnect();
    }

    try {
      const wsUrl = getWebSocketUrl();
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        setIsConnected(true);
        setError(null);
        console.log('Client WebSocket connected');
      };
      
      wsRef.current.onmessage = handleMessage;
      
      wsRef.current.onclose = () => {
        setIsConnected(false);
        setIsRecording(false);
        setIsThinking(false);
        console.log('Client WebSocket disconnected');
      };
      
      wsRef.current.onerror = (err) => {
        setError('Connection failed. Please try again.');
        console.error('WebSocket error:', err);
      };
    } catch (err) {
      setError('Failed to connect. Please check your internet connection.');
      console.error('Connection error:', err);
    }
  }, [getWebSocketUrl, handleMessage]);
  
  // Disconnect WebSocket
  const disconnect = useCallback(async () => {
    if (captureRef.current) {
      try { await captureRef.current.stop(); } catch (_) { /* ignore */ }
      captureRef.current = null;
    }

    if (wsRef.current) {
      try { wsRef.current.close(); } catch (_) { /* ignore */ }
      wsRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try { await audioContextRef.current.close(); } catch (_) { /* ignore */ }
      audioContextRef.current = null;
    }

    setIsConnected(false);
    setIsRecording(false);
    setTranscript([]);
    setIsThinking(false);
  }, []);
  
  // Start recording (raw 16-bit PCM @ 16 kHz via AudioWorklet)
  const startRecording = useCallback(async () => {
    if (!isConnected || isRecording) return;

    try {
      const cap = new PCMCapture({
        onChunk: (b64) => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'audio', audio: b64 }));
          }
        },
        onError: (e) => {
          console.error('PCMCapture error:', e);
          setError(e?.message || 'Microphone error');
        },
        onReady: ({ sampleRate }) =>
          console.log(`[PCMCapture] ready — sampleRate=${sampleRate}`),
      });

      await cap.start();
      captureRef.current = cap;
      setIsRecording(true);
      setError(null);
    } catch (err) {
      setError('Microphone access denied. Please allow microphone access and try again.');
      console.error('Recording error:', err);
    }
  }, [isConnected, isRecording]);
  
  // Stop recording
  const stopRecording = useCallback(async () => {
    if (captureRef.current) {
      try { await captureRef.current.stop(); } catch (_) { /* ignore */ }
      captureRef.current = null;
    }
    setIsRecording(false);
  }, []);
  
  // Toggle recording
  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);
  
  // Show loading state during validation
  if (isValidating) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="bg-slate-900 border-slate-800 w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-8 h-8 mx-auto text-teal-400 animate-spin mb-4" />
            <p className="text-white">Validating your session...</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Show error if validation failed
  if (validationError) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="bg-slate-900 border-slate-800 w-full max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-4" />
            <h2 className="text-xl font-semibold text-white mb-4">Session Expired</h2>
            <p className="text-slate-400 mb-6">{validationError}</p>
            <div className="text-sm text-slate-500">
              <Shield className="w-4 h-4 inline mr-1" />
              Your privacy is protected
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const industryColor = industryColors[sessionData?.industry_type] || '#6B7280';
  const industryBadgeClass = industryBadgeColors[sessionData?.industry_type] || 'bg-slate-500/20 text-slate-400';

  return (
    <div className="min-h-screen bg-slate-950" style={{ '--industry-color': industryColor }}>
      {/* Header */}
      <div className="bg-slate-900/50 border-b border-slate-800 p-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-white">{sessionData?.tenant_name}</h1>
              {sessionData?.industry_type && (
                <Badge className={cn("mt-1", industryBadgeClass)}>
                  {sessionData.industry_type}
                </Badge>
              )}
            </div>
            <div className="text-right">
              <div className="flex items-center space-x-2 text-sm">
                {isConnected ? (
                  <><Wifi className="w-4 h-4 text-green-400" /><span className="text-green-400">Connected</span></>
                ) : (
                  <><WifiOff className="w-4 h-4 text-slate-400" /><span className="text-slate-400">Disconnected</span></>
                )}
              </div>
            </div>
          </div>
          
          {/* Language support message */}
          <div className="mt-3 flex items-center space-x-2 text-sm text-slate-400">
            <Globe className="w-4 h-4" />
            <span>Supports 70+ languages — speak in your preferred language</span>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-2xl mx-auto p-4 space-y-6">
        {/* Error Alert */}
        {error && (
          <Alert className="bg-red-500/10 border-red-500/20 text-red-400">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {/* Voice Interface */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-8 text-center">
            {!isConnected ? (
              <div>
                <div className="w-24 h-24 mx-auto rounded-full bg-slate-800 border-2 border-slate-700 flex items-center justify-center mb-6">
                  <Mic className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-white mb-2">Ready to Connect</h3>
                <p className="text-slate-400 mb-6">Click connect to start your voice session</p>
                <Button 
                  onClick={connect}
                  className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 text-lg"
                  data-testid="connect-button"
                >
                  Connect
                </Button>
              </div>
            ) : (
              <div>
                <div className="relative">
                  <Button
                    onClick={toggleRecording}
                    className={cn(
                      "w-24 h-24 rounded-full text-white transition-all duration-300 mb-6",
                      isRecording
                        ? "bg-red-500 hover:bg-red-600 animate-pulse"
                        : "bg-teal-500 hover:bg-teal-600"
                    )}
                    data-testid="microphone-button"
                  >
                    {isRecording ? (
                      <MicOff className="w-8 h-8" />
                    ) : (
                      <Mic className="w-8 h-8" />
                    )}
                  </Button>
                  
                  {/* Recording indicator */}
                  {isRecording && (
                    <div className="absolute -inset-2 rounded-full border-4 border-red-400/30 animate-ping"></div>
                  )}
                </div>
                
                <h3 className="text-lg font-medium text-white mb-2">
                  {isRecording ? 'Listening...' : 'Tap to Speak'}
                </h3>
                
                {isThinking && (
                  <div className="flex items-center justify-center space-x-2 text-teal-400 mb-4">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Processing...</span>
                  </div>
                )}
                
                <div className="flex justify-center space-x-4">
                  <Button
                    variant="outline"
                    onClick={disconnect}
                    className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
                    data-testid="disconnect-button"
                  >
                    Disconnect
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Transcript */}
        {transcript.length > 0 && (
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6">
              <h3 className="text-lg font-medium text-white mb-4 flex items-center">
                <CheckCircle className="w-5 h-5 mr-2 text-teal-400" />
                Conversation
              </h3>
              <div className="space-y-3 max-h-80 overflow-y-auto" data-testid="transcript-area">
                {transcript.map((entry) => (
                  <div key={entry.id} className="text-sm">
                    <div className="flex items-start space-x-2">
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "px-2 py-1 text-xs",
                          entry.speaker === 'user' 
                            ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                            : "bg-teal-500/20 text-teal-400 border-teal-500/30"
                        )}
                      >
                        {entry.speaker === 'user' ? 'You' : 'Assistant'}
                      </Badge>
                      <div className="flex-1">
                        <p className="text-white leading-relaxed">{entry.text}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(entry.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Footer */}
      <div className="text-center py-8 text-sm text-slate-500">
        <div className="flex items-center justify-center space-x-2">
          <Shield className="w-4 h-4" />
          <span>Powered by DD Universal Voice</span>
        </div>
      </div>
    </div>
  );
};

export default ClientIntake;